export const TOOL_IDS = [
  "pallet-roi",
  "gaylord-volume",
  "sustainability-impact",
  "diversion-savings",
  "pickup-frequency",
  "truck-loading",
  "freight-calculator",
  "packaging-designer",
  "grade",
  "price-index",
] as const;

export type ToolId = (typeof TOOL_IDS)[number];

export type ToolGroup = "tool" | "calculator";

interface ToolDefinition {
  title: string;
  dashboardTitle?: string;
  description: string;
  group: ToolGroup;
  publicPath: string;
  dashboardPath: string;
  defaultSegment?: string;
  listedInResourceCalculators: boolean;
}

export interface Tool extends ToolDefinition {
  id: ToolId;
}

const TOOLS: Record<ToolId, ToolDefinition> = {
  "pallet-roi": {
    title: "Pallet ROI",
    description: "Cost per pallet and per year, new vs. reclaimed",
    group: "calculator",
    publicPath: "/resources/calculators/pallet-roi",
    dashboardPath: "/dashboard/tools/pallet-roi",
    listedInResourceCalculators: true,
  },
  "gaylord-volume": {
    title: "Gaylord volume",
    description: "Usable volume for shipment and storage planning",
    group: "calculator",
    publicPath: "/resources/calculators/gaylord-volume",
    dashboardPath: "/dashboard/tools/gaylord-volume",
    listedInResourceCalculators: true,
  },
  "sustainability-impact": {
    title: "Sustainability impact",
    description: "Landfill diversion and carbon impact from reclaimed packaging",
    group: "calculator",
    publicPath: "/resources/calculators/sustainability-impact",
    dashboardPath: "/dashboard/tools/sustainability-impact",
    listedInResourceCalculators: true,
  },
  "diversion-savings": {
    title: "Diversion savings",
    description: "Avoided disposal costs from a recycling program",
    group: "calculator",
    publicPath: "/resources/calculators/diversion-savings",
    dashboardPath: "/dashboard/tools/diversion-savings",
    listedInResourceCalculators: true,
  },
  "pickup-frequency": {
    title: "Pickup frequency",
    description: "How often to schedule recycling pickups",
    group: "calculator",
    publicPath: "/resources/calculators/pickup-frequency",
    dashboardPath: "/dashboard/tools/pickup-frequency",
    listedInResourceCalculators: true,
  },
  "truck-loading": {
    title: "Truck loading",
    description: "How many units fit on each truck, with a 3D stack view",
    group: "calculator",
    publicPath: "/resources/calculators/truck-loading",
    dashboardPath: "/dashboard/tools/truck-loading",
    defaultSegment: "pallets",
    listedInResourceCalculators: false,
  },
  "freight-calculator": {
    title: "Freight calculator",
    dashboardTitle: "Freight quote",
    description: "LTL or full-truckload freight estimate between two ZIP codes",
    group: "tool",
    publicPath: "/freight-calculator",
    dashboardPath: "/dashboard/tools/freight",
    listedInResourceCalculators: true,
  },
  "packaging-designer": {
    title: "Packaging designer",
    dashboardTitle: "Spec sheet generator",
    description: "Design pallets, boxes, crates, drums, totes, bags and spools in 3D and get a spec sheet",
    group: "tool",
    publicPath: "/packaging-designer",
    dashboardPath: "/dashboard/spec-sheets",
    listedInResourceCalculators: true,
  },
  "grade": {
    title: "Packaging grader",
    description: "Photograph used packaging for its condition grade and what buyers pay for it",
    group: "tool",
    publicPath: "/grade",
    dashboardPath: "/dashboard/tools/grade",
    listedInResourceCalculators: true,
  },
  "price-index": {
    title: "Price index",
    description: "What buyers pay by product, state and grade, and whether a quoted price is in line",
    group: "tool",
    publicPath: "/price-index",
    dashboardPath: "/dashboard/tools/price-index",
    listedInResourceCalculators: true,
  },
};

export const TOOL_CATALOG: ReadonlyArray<Tool> = TOOL_IDS.map((id) => ({ id, ...TOOLS[id] }));

function withDefaultSegment(path: string, tool: Tool): string {
  return tool.defaultSegment ? `${path}/${tool.defaultSegment}` : path;
}

export function toolPublicHref(tool: Tool): string {
  return withDefaultSegment(tool.publicPath, tool);
}

export function toolDashboardHref(tool: Tool): string {
  return withDefaultSegment(tool.dashboardPath, tool);
}

export function toolDashboardTitle(tool: Tool): string {
  return tool.dashboardTitle ?? tool.title;
}

export function getTool(id: ToolId): Tool {
  return { id, ...TOOLS[id] };
}
